import { useState, useEffect, useCallback, useMemo } from "react";
import React from "react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Button } from "../../../components/ui/button"
import { ChevronLeft, ChevronRight, ArrowUpDown, Edit2, Trash2, MoreHorizontal, Eye, FileText } from "lucide-react";
import "../../../components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip"
import "../../../components/ui/skeleton";
import { toast } from "react-toastify"
import { motion, AnimatePresence } from "framer-motion"
import { formatCellValue } from "../../../../utils/cellValueFormatter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";

export default function ControlPanelTable({
  data: initialData,
  onRowClick,
  loading: externalLoading,
  onRefresh,
  onEdit,
  onDelete,
}) {
  const [data, setData] = useState(initialData || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const itemsPerPage = 10;

  // Usar directamente el loading externo, no mantener estado interno
  const loading = externalLoading;

  useEffect(() => {
    setData(initialData || []);
  }, [initialData]);

  const handleSort = useCallback((key) => {
    if (key === "actions") return;

    setSortConfig((prevConfig) => {
      if (prevConfig.key === key) {
        return {
          key,
          direction: prevConfig.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  }, []);

  const handleEdit = useCallback(
    (e, row) => {
      e.stopPropagation();
      if (onEdit) {
        onEdit(row);
      } else {
        toast.info(`Editar IPR: ${row.id || "N/A"}`);
      }
    },
    [onEdit]
  );

  const handleDelete = useCallback(
    (e, row) => {
      e.stopPropagation();
      if (onDelete) {
        onDelete(row);
      } else {
        toast.warn(`Eliminar IPR: ${row.id || "N/A"}`);
      }
    },
    [onDelete]
  );


  const sortedData = useMemo(() => {
    let result = [...data];

    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === bValue) return 0;
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        const comparison = String(aValue).localeCompare(String(bValue));
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    return result;
  }, [data, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  const getColumnLabel = (key) => {
    if (key === "actions") return "";

    const labelMap = {
      id: "ID",
      friendly_id: "ID Amigable",
      origin_channel: "Canal",
      estado: "Estado",
      ai_summary: "Resumen IA",
      created_by_name: "Creador",
      created_at: "Fecha de Creación",
      area: "Área",
      title: "Título",
      source_email: "Email Origen",
      language: "Idioma",
      validations: "Validaciones",
      extraction_date: "Fecha de Extracción",
      total_amount: "Monto Total",
      validation_status: "Estado de Validación",
      status: "Estado",
      step: "Paso",
      hasErrors: "Errores",
      isVerified: "Verificado",
    };

    return (
      labelMap[key] ||
      key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };

  const getTableColumns = () => {
    if (data.length === 0) return ["actions"];
    // Definir columnas prioritarias para mejor presentación
    const priorityColumns = [
      "id",
      "origin_channel",
      "validation_status",
      "created_by_name",
      "created_at",
      "area",
      "title",
      "total_amount",
      "extraction_date",
      "status",
    ];
    const hiddenColumns = ["created_by", "updated_at"];

    // Filtrar solo las columnas que existen en los datos y son prioritarias
    const allColumns = Object.keys(data[0]);
    const visibleColumns = priorityColumns.filter((col) =>
      allColumns.includes(col)
    );

    // Añadir otras columnas importantes que puedan existir (custom columns)
    const additionalColumns = allColumns.filter(
      (col) =>
        !priorityColumns.includes(col) &&
        !["hasErrors", "isVerified", "date", "status", "step"].includes(col) &&
        !hiddenColumns.includes(col) &&
        typeof data[0][col] !== "object" &&
        data[0][col] !== null &&
        data[0][col] !== undefined &&
        String(data[0][col]).trim() !== ""
    );

    return [...visibleColumns, ...additionalColumns, "actions"];
  };

  return (
    <TooltipProvider>
      <div className="overflow-hidden">
        <div className="overflow-x-auto scrollbar-clean">
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700 border-b border-slate-200 dark:border-slate-700">
                {getTableColumns().map((key) => (
                  <TableHead
                    key={key}
                    className={`py-4 px-6 text-left font-semibold text-slate-700 dark:text-slate-300 transition-all duration-200 ${
                      key === "actions"
                        ? "w-24 text-center"
                        : "cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50"
                    }`}
                    onClick={() => handleSort(key)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">
                        {getColumnLabel(key)}
                      </span>
                      {key !== "actions" && (
                        <div className="flex items-center ml-2">
                          {sortConfig.key === key ? (
                            <ArrowUpDown
                              className={`h-4 w-4 text-orange-600 dark:text-orange-400 transition-transform duration-200 ${
                                sortConfig.direction === "desc"
                                  ? "rotate-180"
                                  : ""
                              }`}
                            />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-75 transition-opacity duration-200" />
                          )}
                        </div>
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <AnimatePresence>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={getTableColumns().length}
                      className="text-center py-12"
                    >
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center justify-center space-x-3"
                      >
                        <div className="loading-clean">
                          <div className="spinner"></div>
                        </div>
                        <span className="text-slate-500 font-medium">
                          Cargando registros...
                        </span>
                      </motion.div>
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={getTableColumns().length}
                      className="text-center py-16"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col items-center space-y-4"
                      >
                        <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                          <FileText className="w-8 h-8 text-slate-400" />
                        </div>
                        <div className="text-center">
                          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">
                            No hay registros disponibles
                          </h3>
                          <p className="text-slate-500 dark:text-slate-400 text-sm max-w-sm">
                            Los IPRs aparecerán aquí cuando estén disponibles.
                            Intenta actualizar o crear un nuevo registro.
                          </p>
                        </div>
                      </motion.div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row, index) => (
                    <motion.tr
                      key={row.id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-all duration-200 border-b border-slate-100 dark:border-slate-800 last:border-b-0 group"
                      onClick={() => onRowClick && onRowClick(row)}
                    >
                      {getTableColumns()
                        .filter((key) => key !== "actions")
                        .map((key) => (
                          <TableCell key={key} className="px-6 py-4">
                            {formatCellValue(key, row[key], row)}
                          </TableCell>
                        ))}

                      <TableCell className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRowClick && onRowClick(row);
                                }}
                                className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 transition-colors duration-200"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Ver detalles</p>
                            </TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleEdit(e, row)}
                                className="h-8 w-8 p-0 hover:bg-orange-50 hover:text-orange-600 dark:hover:bg-orange-900/20 transition-colors duration-200"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Editar IPR</p>
                            </TooltipContent>
                          </Tooltip>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRowClick && onRowClick(row);
                                }}
                                className="cursor-pointer"
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Ver detalles
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => handleEdit(e, row)}
                                className="cursor-pointer"
                              >
                                <Edit2 className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => handleDelete(e, row)}
                                className="cursor-pointer text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </motion.tr>
                  ))
                )}
              </AnimatePresence>
            </TableBody>
          </Table>
        </div>

        {/* Enhanced Pagination */}
        {sortedData.length > itemsPerPage && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50/50 to-slate-100/50 dark:from-slate-800/50 dark:to-slate-700/50"
          >
            <div className="flex items-center space-x-2">
              <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                Mostrando{" "}
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {startIndex + 1}
                </span>{" "}
                a{" "}
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {Math.min(startIndex + itemsPerPage, sortedData.length)}
                </span>{" "}
                de{" "}
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {sortedData.length}
                </span>{" "}
                registros
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn-clean btn-secondary h-9 px-3"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>

              <div className="flex items-center px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-sm font-medium text-slate-700 dark:text-slate-300">
                Página {currentPage} de {totalPages}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn-clean btn-secondary h-9 px-3"
              >
                Siguiente
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </TooltipProvider>
  );
} 